import { Avatar, AvatarProps, Skeleton } from '@mui/material';
import { baseApi } from 'api';
import React, { memo, useCallback, useEffect, useState } from 'react';

interface AvatarRemProps extends AvatarProps {
  icon?: string;
}

const imageCache = new Map<string, string>();
const pendingRequests = new Map<string, Promise<string>>();
const AvatarRem: React.FC<AvatarRemProps> = ({ icon, ...rest }) => {
  const [image, setImage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    if (!icon) {
      setImage("");
      setLoading(false);
      return;
    }

    if (imageCache.has(icon)) {
      setImage(imageCache.get(icon)!);
      setLoading(false);
      return;
    }

    if (pendingRequests.has(icon)) {
      setLoading(true);
      try {
        const cachedImage = await pendingRequests.get(icon)!;
        setImage(cachedImage);
      } catch {
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    const request = baseApi.get(icon)
      .then(res => {
        if (res.data) {
          imageCache.set(icon, res.data);
          return res.data;
        }
        throw new Error('No data');
      })
      .finally(() => {
        pendingRequests.delete(icon);
      });

    pendingRequests.set(icon, request);

    try {
      const imageData = await request;
      setImage(imageData);
    } catch {
      setImage("");
    } finally {
      setLoading(false);
    }
  }, [icon]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Avatar
      {...rest}
      src={image}
      sx={{ position: "relative", ...rest.sx }}
    >
      {loading ? (
        <Skeleton
          variant='circular'
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      ) : (
        rest.children
      )}
    </Avatar>
  );
};

export default memo(AvatarRem, (p, n) => {
  return p.icon === n.icon
});